import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/IframeAuthContext';
import { getAuthTokensService, type TokensDTO } from '../lib/auth/AuthTokensService';

export const CallbackPage: React.FC = () => {
    const { isAuthenticated } = useAuth();
    const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const processCallback = async () => {
            try {
                // Get query parameters
                const urlParams = new URLSearchParams(window.location.search);
                const token = urlParams.get('token');
                const refreshToken = urlParams.get('refresh_token');
                const errorParam = urlParams.get('error');

                console.log('Callback processing:', {
                    token: token ? 'present' : 'missing',
                    refreshToken: refreshToken ? 'present' : 'missing',
                    error: errorParam
                });

                if (errorParam) {
                    setError(errorParam);
                    setStatus('error');
                    return;
                }

                if (token) {
                    // Store tokens using the tokens service
                    const authTokensService = getAuthTokensService();

                    // Decode the JWT to get roles and tenant_id
                    let roles: string[] = [];
                    let tenantId = '';

                    try {
                        const payload = JSON.parse(atob(token.split('.')[1]));
                        roles = payload.roles || [];
                        tenantId = payload.tenant_id || '';
                        console.log('Decoded JWT payload:', { roles, tenantId });
                    } catch (e) {
                        console.warn('Failed to decode JWT token:', e);
                    }

                    const credentials: TokensDTO = {
                        access_token: {
                            token: token,
                            roles: roles,
                            tenant_id: tenantId,
                            exp: '', // Will be populated by jwt decode in Refresh logic if needed
                        },
                        refresh_token: {
                            token: refreshToken || '',
                            exp: '',
                        },
                    };

                    console.log('Setting credentials...');
                    await authTokensService.setCredentials(credentials);
                    console.log('Credentials set successfully');

                    setStatus('success');

                    // Redirect to stored path or root after a brief delay
                    setTimeout(() => {
                        const redirectFromUrl = urlParams.get('redirect');
                        const redirectPath = redirectFromUrl || localStorage.getItem('loginRedirectPath') || '/';

                        localStorage.removeItem('loginRedirectPath'); // Clean up
                        console.log('CallbackPage: Redirecting to:', redirectPath);
                        window.location.href = redirectPath;
                    }, 1000);
                } else {
                    setError('No authentication token received');
                    setStatus('error');
                }
            } catch (err) {
                console.error('Callback processing error:', err);
                setError('Failed to process authentication callback');
                setStatus('error');
            }
        };

        processCallback();
    }, []);

    // If already authenticated (from iframe), redirect to stored path or root
    useEffect(() => {
        if (isAuthenticated && status === 'processing') {
            const urlParams = new URLSearchParams(window.location.search);
            const redirectFromUrl = urlParams.get('redirect');
            const redirectPath = redirectFromUrl || localStorage.getItem('loginRedirectPath') || '/';

            localStorage.removeItem('loginRedirectPath'); // Clean up
            console.log('CallbackPage: Already authenticated, redirecting to:', redirectPath);
            window.location.href = redirectPath;
        }
    }, [isAuthenticated, status]);

    if (status === 'processing') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Processing authentication...</p>
                </div>
            </div>
        );
    }

    if (status === 'success') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Successful</h2>
                    <p className="text-gray-600 mb-4">Redirecting to application...</p>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                </div>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Failed</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={() => window.location.href = '/login'}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return null;
};

export default CallbackPage;
